import React, { useEffect, useState } from "react";
import { TableSelectProps } from "./tableSelect.types";

const colorConfig = {
    green: {
        headerBg: "bg-[#4d797f]",
        tableBorder: "border-[#d9e6e8]",
        cellBorder: "border-[#d9e6e8]",
        zebraBg: "bg-[#f0f7f8]",
        cellText: "text-[#314d51]",
        accentBorder: "border-[#4d797f]",
        accentDot: "bg-[#4d797f]",
        checkBg: "bg-[#4d797f]",
    },
    orange: {
        headerBg: "bg-[#ba6520]",
        tableBorder: "border-[#fae7d7]",
        cellBorder: "border-[#fae7d7]",
        zebraBg: "bg-[#fff4e6]",
        cellText: "text-[#925016]",
        accentBorder: "border-[#ba6520]",
        accentDot: "bg-[#ba6520]",
        checkBg: "bg-[#ba6520]",
    },
} as const;

export const TableSelect: React.FC<TableSelectProps> = ({ ...props }) => {
    const [selected, setSelected] = useState<string[]>([]);
    const c = colorConfig[props.color ?? "green"];

    const isDisabled = (value: string) => props.disabledValues?.includes(value) ?? false;

    const handleOnCheckboxClick = (value: string) => {
        if (isDisabled(value)) return;
        const next = selected.includes(value)
            ? selected.filter(v => v !== value)
            : [...selected, value];
        setSelected(next);
        props.onChange?.(next);
    };

    const handleOnRadioClick = (value: string) => {
        if (isDisabled(value)) return;
        const next = selected.includes(value) ? [] : [value];
        setSelected(next);
        props.onChange?.(next);
    };

    useEffect(() => {
        if (props.value) setSelected(props.value);
        else if (props.defaultValue) setSelected(props.defaultValue);
    }, [props.value]);

    return (
        <div className="w-full overflow-hidden rounded-xl border border-neutral-200 shadow-sm">
            <table className={`w-full border-collapse text-sm font-[Manrope] bg-white border ${c.tableBorder}`}>
                <thead className="uppercase">
                    <tr>
                        <th className={`w-10 px-3 py-3 ${c.headerBg}`} />
                        {props.headers.map((h, idx) => (
                            <th
                                key={idx}
                                className={`px-3 py-3 text-left text-xs font-bold tracking-wide text-white ${c.headerBg}`}
                            >
                                {h}
                            </th>
                        ))}
                        <th className={`w-10 px-3 py-3 ${c.headerBg}`} />
                    </tr>
                </thead>
                <tbody>
                    {props.data?.map((d, index) => {
                        const isSelected = selected.includes(d.value);
                        const disabled = isDisabled(d.value);
                        const rowBg = index % 2 === 0 ? c.zebraBg : "bg-white";
                        const isLastRow = index === (props.data?.length ?? 0) - 1;

                        return (
                            <tr key={index} className="hover:brightness-[0.98] transition-all">
                                <td className={`w-10 px-3 py-2.5 ${rowBg} ${!isLastRow ? `border-b ${c.cellBorder}` : ""}`}>
                                    <div className="flex items-center justify-center">
                                        {props.type === "checkbox" ? (
                                            <div
                                                onClick={() => handleOnCheckboxClick(d.value)}
                                                className={[
                                                    "w-5 h-5 rounded-[5px] border-2 flex items-center justify-center cursor-pointer transition-all duration-200",
                                                    isSelected
                                                        ? `${c.accentBorder} ${c.checkBg}`
                                                        : "border-slate-300 hover:" + c.accentBorder,
                                                    disabled ? "cursor-not-allowed opacity-45 border-slate-300 bg-slate-50" : "",
                                                ].join(" ")}
                                            >
                                                {isSelected && (
                                                    <span className="text-white text-xs leading-none select-none">✓</span>
                                                )}
                                            </div>
                                        ) : (
                                            <div
                                                onClick={() => handleOnRadioClick(d.value)}
                                                className={[
                                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all duration-200",
                                                    isSelected
                                                        ? c.accentBorder
                                                        : "border-slate-300",
                                                    disabled ? "cursor-not-allowed opacity-45 border-slate-300 bg-slate-50" : "",
                                                ].join(" ")}
                                            >
                                                {isSelected && (
                                                    <span className={`w-[9px] h-[9px] rounded-full ${c.accentDot}`} />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                {d.labels.map((label, idx) => (
                                    <td
                                        key={idx}
                                        className={[
                                            `px-3 py-2.5 text-sm ${c.cellText} ${rowBg}`,
                                            !isLastRow ? `border-b ${c.cellBorder}` : "",
                                        ].join(" ")}
                                    >
                                        {label}
                                    </td>
                                ))}
                                <td className={`w-10 ${rowBg} ${!isLastRow ? `border-b ${c.cellBorder}` : ""}`} />
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};